import { inTestEnvironment } from '../util/environment';

up.compiler('[autosave]', function(element, data) {
  const saveInterval = data.autosaveIntervalSeconds * 1000; // microseconds
  const bootTimeout = (inTestEnvironment()) ? 1 : 1500;
  const {
    newNote,
    noteId,
    siteId,
    enableUnchangedSnapshots
  } = data;
  const form = element.querySelector('.autosave--form');
  const inputs = element.querySelectorAll('.autosave--input');
  const cancelButton = element.querySelector('.autosave--cancel');
  const reloadButton = element.querySelector('.autosave--reload');
  const lastTextEditedAt = element.querySelector('.autosave--last-text-edited-at');
  let timeout = undefined;
  const cleanupAfter = 7 * 24 * 60 * 60 * 1000; // one week in ms

  const cleanupSnapshots = () => {
    for (let i = 0; i < localStorage.length; i++) {
      const localStorageKey = localStorage.key(i);

      if (localStorageKey && localStorageKey.match('^autosave_note_')) {
        const snapshot = LocalJsonStorage.getItem(localStorageKey);
        const snapshotDate = new Date(snapshot.timestamp);
        if (((new Date()) - snapshotDate) > cleanupAfter) {
          LocalJsonStorage.removeItem(localStorageKey);
        }
      }
    }
  };

  const buildSnapshot = function() {
    const snapshot = {
      timestamp: Date.now(),
      lastTextEditedAt: lastTextEditedAt.value
    };
    const payload = {};
    up.util.each(inputs, function(input, index) {
      const name = input.getAttribute('name');
      const value = input.value;
      payload[name] = value;
    });
    snapshot['payload'] = payload;
    return snapshot;
  };

  const saveNoteChanges = function() {
    const currentSnapshot = buildSnapshot();
    if (enableUnchangedSnapshots || snapshotsDiffer(snapshotOnPageLoad, currentSnapshot)) {
      LocalJsonStorage.setItem(storageKeyForNote(), currentSnapshot);
    }
    schedule();
  };

  var schedule = () => timeout = setTimeout(saveNoteChanges, saveInterval);

  const reset = () => clearTimeout(timeout);

  var storageKeyForNote = function() {
    if (newNote) {
      return `autosave_note_new_on_site_${siteId}`;
    } else {
      return `autosave_note_${noteId}`;
    }
  };

  const hasChangedSnapshot = function() {
    const savedSnapshot = LocalJsonStorage.getItem(storageKeyForNote());
    const currentSnapshot = buildSnapshot();
    return snapshotsDiffer(savedSnapshot, currentSnapshot);
  };

  var snapshotsDiffer = function(snapshot, otherSnapshot) {
    if (!snapshot) { return false; }
    if (!otherSnapshot) { return false; }
    return !up.util.isEqual(snapshot.payload, otherSnapshot.payload);
  };

  // do not restore, if saved snapshot was started with a different lastTextEditedAt (i.e. another user edited note in the meantime)
  const hasIncompatibleSnapshot = function() {
    const savedSnapshot = LocalJsonStorage.getItem(storageKeyForNote());
    if (!savedSnapshot) { return false; }
    // A snapshot is counted as compatible, if lastTextEditedAt is missing.
    // This will only occur for old snapshots before this commit.
    // Old snapshots (only valid for 7 days) will be able to accidentally override updates for up to 1 week after the deploy.
    // It was decided that this would be acceptable. Otherwise all old snapshots would have been lost immediately on deploy.
    if (!savedSnapshot.lastTextEditedAt) { return false; }
    return !up.util.isEqual(savedSnapshot.lastTextEditedAt, lastTextEditedAt.value);
  };

  const checkRestoreSnapshot = function() {
    if (hasChangedSnapshot()) {
      const savedSnapshot = LocalJsonStorage.getItem(storageKeyForNote());
      const savedDate = new Date(savedSnapshot.timestamp);
      const confirmText = newNote ?
        `You started a new card for this deck on ${savedDate.toLocaleString()}. Do you want to restore these changes? Otherwise the changes will be discarded.`
      : hasIncompatibleSnapshot() ?
        `You edited this note on ${savedDate.toLocaleString()}.\n\nAnother user has edited this note in the meantime.\n\nDo you want to restore your changes? Afterwards, please copy your changes, reload the page and then merge your changes into the new version.\n\nClicking \"Cancel\" will discard your changes.`
      :
        `You edited this note on ${savedDate.toLocaleString()}. Do you want to restore these changes? Otherwise the changes will be discarded.`;
      if (confirm(confirmText)) {
        restoreSnapshot(savedSnapshot);
      }
      discardSnapshot();
    }
  };

  var restoreSnapshot = function(snapshot) {
    Object.entries(snapshot.payload).forEach(([inputName, value]) => {
      const inputElement = up.util.find(inputs, element => element.getAttribute('name') === inputName);
      if (inputElement !== null) {
        inputElement.value = value;
      }
    });
    lastTextEditedAt.value = snapshot.lastTextEditedAt;
  };

  var discardSnapshot = () => LocalJsonStorage.removeItem(storageKeyForNote());

  var snapshotOnPageLoad = buildSnapshot();
  up.on(cancelButton, 'click', () => discardSnapshot());
  if (reloadButton !== null) {
    up.on(reloadButton, 'click', () => discardSnapshot());
  }
  up.on(form, 'submit', () => discardSnapshot());
  cleanupSnapshots();

  const start = function() {
    checkRestoreSnapshot();
    schedule();
  };
  setTimeout(start, bootTimeout); // timeout to make sure all components have been loaded and rendered correctly

  return () => reset();
});


//#
// A wrapper around window.localStorage that automatically
// serializes items from/to JSON.
//
class LocalJsonStorage {
  static getItem(prop) {
    let item = localStorage.getItem(prop);
    if (up.util.isString(item)) { item = JSON.parse(item); }
    return item;
  }
  static setItem(prop, value) {
    value = JSON.stringify(value);
    return localStorage.setItem(prop, value);
  }
  static removeItem(prop) {
    return localStorage.removeItem(prop);
  }
}
